import React, { useEffect, useState } from 'react';
import { Button, Form, Select } from 'antd';
import { CrownOutlined, EyeOutlined, TeamOutlined } from '@ant-design/icons';
import { useDataProvider, useNotify } from 'react-admin';
import { ExtendedDataProvider } from '@src/types';
import DebounceSelect from '@src/components/Common/DebounceSelect';

const { Option } = Select;

/**
 * The three roles an LDAP group can be granted, strongest first, each with what
 * it actually lets someone do.
 *
 * That sentence used to live in a "How it works?" accordion above the form,
 * where it explained all three roles at once to someone who had not yet been
 * asked about any of them. It belongs beside the field that hands the role out:
 * you read what ADMIN means at the moment you are choosing who gets it.
 */
const ROLES = [
    {
        name: 'ADMIN',
        label: 'Admin',
        accent: '#c98a12',
        icon: <CrownOutlined />,
        what: 'View and change everything the team owns, including these settings.',
    },
    {
        name: 'GENERAL',
        label: 'General',
        accent: 'var(--tm-primary, #5796cc)',
        icon: <TeamOutlined />,
        what: 'View everything. On a service owner team, also process change instances.',
    },
    {
        name: 'READ_ONLY',
        label: 'Read only',
        accent: '#8a93a6',
        icon: <EyeOutlined />,
        what: 'View everything the team owns, and change none of it.',
    },
];

/**
 * Who is on this team, decided by LDAP rather than by invitation. One group per
 * role: whoever is in the group gets the role, and a role with no group behind
 * it is granted to nobody.
 */
const MappingsTab = ({ teamId, allTeams = false }: { teamId: number; allTeams?: boolean }) => {
    const notify = useNotify();
    const [form] = Form.useForm();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const [saving, setSaving] = useState(false);

    // what is on the cards right now, so each one can say whether it is in force
    const values = Form.useWatch([], form) || {};

    const fetchMappings = () =>
        dataProvider
            .getAction('teams', {
                id: teamId,
                verb: 'team_mappings',
                /* a platform admin reads another team's mappings with all_teams; the
                   write below names the team in the URL and needs no flag */
                ...(allTeams ? { filter: { all_teams: true } } : {}),
            })
            .then((response: any) =>
                form.setFieldsValue({
                    ADMIN: response.data?.ADMIN?.name,
                    GENERAL: response.data?.GENERAL?.name,
                    READ_ONLY: response.data?.READ_ONLY?.name,
                }),
            )
            .catch(() => notify('Error fetching team mappings', 'error'));

    useEffect(() => {
        fetchMappings();
    }, []);

    const save = (submitted: any) => {
        // a role left blank is granted to nobody, which the API is told as null
        const payload: Record<string, any> = {};
        ROLES.forEach(({ name }) => {
            payload[name] = submitted[name] || null;
        });

        setSaving(true);
        dataProvider
            .createAction('teams', { data: payload, id: teamId, verb: 'team_mappings/' })
            .then(() => {
                notify('Team mappings updated');
                fetchMappings();
            })
            .catch((error: any) => {
                if (error.status === 400 && error.body?.non_field_errors) {
                    notify(String(error.body.non_field_errors), 'error');
                    return;
                }
                notify('Error updating team mappings', 'error');
            })
            .finally(() => setSaving(false));
    };

    const fetchGroups = async (search: string): Promise<any> =>
        dataProvider
            .getList('groups', {
                pagination: { page: 1, perPage: 20 },
                sort: { field: 'name', order: 'asc' },
                filter: { name: search },
            })
            .then((response: any) => response.data.map((group: any) => ({ label: group.name, value: group.id })));

    /* A group NetOrca has not seen yet is still a group: the option stands for
       what was typed, so a mapping can be set up before anyone from it logs in. */
    const typedGroup = (input: string) => (
        <Option key={input} value={input}>
            {input}
        </Option>
    );

    return (
        <div className="tm-tab">
            <p className="tm-note">
                Connect an LDAP group to a role on this team. Everyone in the group gets that role;
                each group can hold one role on one team.
            </p>

            <Form form={form} onFinish={save} layout="vertical" colon={false} className="tm-map">
                <div className="tm-map__grid">
                    {ROLES.map((role) => {
                        const mapped = !!values[role.name];
                        return (
                            <section
                                key={role.name}
                                className={`tm-role${mapped ? ' tm-role--mapped' : ''}`}
                                style={{ ['--tm-role-accent' as any]: role.accent }}
                            >
                                <div className="tm-role__head">
                                    <span className="tm-role__ico" aria-hidden="true">
                                        {role.icon}
                                    </span>
                                    <span className="tm-role__name">{role.label}</span>
                                    <span className="tm-role__state">{mapped ? 'Mapped' : 'Not mapped'}</span>
                                </div>

                                <p className="tm-role__what">{role.what}</p>

                                <Form.Item name={role.name} label="LDAP group">
                                    <DebounceSelect
                                        id={`mapping-${role.name}`}
                                        createable
                                        showSearch
                                        allowClear
                                        placeholder="Search groups"
                                        fetchOptions={fetchGroups}
                                        NotFoundSelectOption={typedGroup}
                                        onChangeSelect={(next: any) =>
                                            form.setFieldsValue({ [role.name]: next?.label })
                                        }
                                    />
                                </Form.Item>
                            </section>
                        );
                    })}
                </div>

                <div className="tm-map__foot">
                    <span className="tm-map__hint">
                        Clearing a group takes the role away from everyone it was granting it to.
                    </span>
                    <Button type="primary" htmlType="submit" loading={saving}>
                        Save mappings
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default MappingsTab;
